import { useMemo, useState } from "react";
import { seo, breadcrumbScript } from "@/lib/seo";
import { Link, createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";
import { ArrowLeft, FlaskConical, Hand, Search } from "lucide-react";
import { LABS, LAB_GROUPS } from "@/lib/labs/registry";
import { cn } from "@/lib/utils";
import { ShareButton } from "@/components/app/share-button";

export const Route = createFileRoute("/labs/")({
  head: () => ({
    ...seo({
      title: "Sim Labs — Physics Virtual Lab for JEE",
      description:
        "Hands-on JEE physics experiments — drag real apparatus, take readings and prove the formula yourself, from vernier calipers to p-n junctions.",
      path: "/labs",
    }),
    scripts: [
      breadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Sim Labs", path: "/labs" },
      ]),
    ],
  }),
  component: LabHub,
});

function LabHub() {
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState<"All" | (typeof LAB_GROUPS)[number]>("All");

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return LABS.filter(
      (l) =>
        (group === "All" || l.group === group) &&
        (!q ||
          l.title.toLowerCase().includes(q) ||
          l.chapter.toLowerCase().includes(q) ||
          l.apparatus.toLowerCase().includes(q)),
    );
  }, [query, group]);

  return (
    <div className="page-shell px-5 lg:px-8 pb-28 pt-8">
      <div className="flex items-center gap-3">
        <Link
          to="/simulations"
          aria-label="Back to simulations"
          className="glass grid size-9 shrink-0 place-items-center rounded-full transition-transform active:scale-95"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div className="min-w-0">
          <p className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
            Physics virtual lab
          </p>
          <h1 className="text-[22px] font-extrabold tracking-tight">Sim Labs</h1>
        </div>
        <ShareButton className="ml-auto" />
      </div>

      <p className="mt-2 flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
        <Hand className="size-3.5 text-primary" />
        Touch the apparatus. Pull, tilt, drop and measure — the physics is real.
      </p>

      <label className="glass mt-4 flex items-center gap-2 rounded-full px-4 py-2.5">
        <Search className="size-4 shrink-0 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search experiments or apparatus"
          aria-label="Search experiments"
          className="w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
        />
      </label>

      <div className="mt-3 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]">
        {(["All", ...LAB_GROUPS] as const).map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => setGroup(g)}
            className={cn(
              "shrink-0 rounded-full px-3.5 py-1.5 text-[12px] font-semibold transition-colors",
              group === g
                ? "[background-image:var(--gradient-brand)] text-primary-foreground"
                : "glass text-muted-foreground",
            )}
          >
            {g}
          </button>
        ))}
      </div>

      <div className="mt-4 card-grid">
        {results.map((lab, i) => (
          <motion.div
            key={lab.id}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(i * 0.04, 0.3), type: "spring", stiffness: 220, damping: 24 }}
            className="glass relative overflow-hidden rounded-[20px]"
          >
            <Link
              to="/labs/$labId"
              params={{ labId: lab.id }}
              className="block p-4 transition-transform active:scale-[0.98]"
            >
              <span
                aria-hidden
                className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-primary/20 to-transparent opacity-60"
              />
              <div className="relative flex items-start gap-3">
                <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-surface/70">
                  <FlaskConical className="size-5 text-primary" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {lab.group} · {lab.chapter}
                  </p>
                  <h2 className="truncate text-[14.5px] font-bold">{lab.title}</h2>
                  <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">{lab.blurb}</p>
                  <p className="mt-1.5 text-[11px] text-primary">{lab.apparatus}</p>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
        {!results.length ? (
          <p className="py-10 text-center text-[12.5px] text-muted-foreground">
            No experiment matches that search.
          </p>
        ) : null}
      </div>
    </div>
  );
}